import { useState } from 'react'
import { Button, FormControl, FormHelperText, Grid, Stack, Typography } from '@vapor/material'
import { TextField } from '@mui/material'
import { useCallback, useMemo } from 'react'
import { FieldError } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { addYears, compareDates, isValidDate } from 'src/utils/date'
import { GuestIdentificationDocument } from '../../../../../types/guests.types'
import { DatePicker, PickerChangeHandlerContext, DateValidationError } from '@mui/x-date-pickers-pro'
import EmissionPlace from './emission-place'
import DocumentSelect from './document-select'
import { HicFormLabel } from 'src/app/components/form-fields/hic-form-label.styled'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faIdCard, faWarning } from '@fortawesome/pro-solid-svg-icons'

export type IdentificationDocumentProps = {
  docsUrls?: string[]
  value: GuestIdentificationDocument | null
  onChange: (value: GuestIdentificationDocument | null) => void
  disabled?: boolean
  comingFromWci: boolean
  error?:
    | FieldError
    | {
        documentId?: FieldError
        number?: FieldError
        expirationDate?: FieldError
        countryId?: FieldError
        city?: FieldError
      }
}

export default function IdentificationDocument({ docsUrls, value, onChange, disabled, error, comingFromWci }: IdentificationDocumentProps) {
  const { t } = useTranslation()
  const documentIdError: FieldError | undefined = useMemo(() => (!error ? undefined : 'message' in error ? error : 'documentId' in error ? error.documentId : undefined), [error])
  const numberError: FieldError | undefined = useMemo(() => (!error ? undefined : 'message' in error ? error : 'number' in error ? error.number : undefined), [error])
  const expirationDateError: FieldError | undefined = useMemo(() => (!error ? undefined : 'message' in error ? error : 'expirationDate' in error ? error.expirationDate : undefined), [error])
  const countryIdError: FieldError | undefined = useMemo(() => (!error ? undefined : 'message' in error ? error : 'countryId' in error ? error.countryId : undefined), [error])
  const cityError: FieldError | undefined = useMemo(() => (!error ? undefined : 'message' in error ? error : 'city' in error ? error.city : undefined), [error])

  const [documentShow, setDocumentShow] = useState<boolean>(false)

  const onLocalChange = useCallback(
    (v: Partial<GuestIdentificationDocument>) => {
      const newValue = checkNull(v)
      if (JSON.stringify(newValue) !== JSON.stringify(value)) {
        onChange(newValue)
      }
    },
    [value, onChange],
  )

  const onChangeExpirationDate = useCallback(
    (d: any, context: PickerChangeHandlerContext<DateValidationError>) => {
      const maxConstraint = addYears(new Date(), 10)
      if (isValidDate(d) && compareDates(d, maxConstraint) < 0) {
        onLocalChange({ ...value, expirationDate: d })
      } else if (!context.validationError?.length) {
        onLocalChange({ ...value, expirationDate: null })
      }
    },
    [onLocalChange, value],
  )

  const documentShowToggleClick = useCallback(() => {
    setDocumentShow(!documentShow)
  }, [documentShow])

  return (
    <Grid item container spacing={1} columns={24}>
      <Grid item xs={4}>
        <FormControl fullWidth error={documentIdError ? true : false}>
          <HicFormLabel error={documentIdError ? true : false}>{t('pms.booking.reservation.guest.documentId')}</HicFormLabel>
          <DocumentSelect //
            size="small"
            disabled={disabled}
            value={value?.documentId ?? null}
            onChange={(documentId) => onLocalChange({ ...value, documentId })}
            error={documentIdError ? true : false}
          />
          {(documentIdError?.message ? true : false) && <FormHelperText error>{documentIdError?.message}</FormHelperText>}
        </FormControl>
      </Grid>
      <Grid item xs={4}>
        <FormControl fullWidth error={numberError ? true : false}>
          <HicFormLabel error={numberError ? true : false}>{t('pms.booking.reservation.guest.number')}</HicFormLabel>
          <TextField //
            size="small"
            disabled={disabled}
            value={value?.number || ''}
            onChange={(event) => {
              const caret = event.target.selectionStart
              const element = event.target
              window.requestAnimationFrame(() => {
                element.selectionStart = caret
                element.selectionEnd = caret
              })
              onLocalChange({ ...value, number: event.target.value })
            }}
            error={numberError ? true : false}
          />
          {(numberError?.message ? true : false) && <FormHelperText error>{numberError?.message}</FormHelperText>}
        </FormControl>
      </Grid>

      <Grid item xs={4}>
        <FormControl fullWidth error={countryIdError ?? cityError ? true : false}>
          <HicFormLabel error={countryIdError ?? cityError ? true : false}>{t('pms.booking.reservation.guest.emission_place')}</HicFormLabel>
          <EmissionPlace //
            size="small"
            value={value?.countryId ? { countryId: value.countryId, city: value?.city ?? null } : null}
            onChange={(emission) => onLocalChange({ ...value, countryId: emission?.countryId ?? null, city: emission?.city ?? null })}
            disabled={disabled}
            error={countryIdError ?? cityError ? true : false}
          />
          {((countryIdError ?? cityError)?.message ? true : false) && <FormHelperText error>{(countryIdError ?? cityError)?.message}</FormHelperText>}
        </FormControl>
      </Grid>

      <Grid item xs={4}>
        <FormControl fullWidth error={expirationDateError ? true : false}>
          <HicFormLabel error={expirationDateError ? true : false}>{t('pms.booking.reservation.guest.expiration_date')}</HicFormLabel>
          <DatePicker //
            value={value?.expirationDate ?? null}
            maxDate={addYears(new Date(), 10)}
            onChange={onChangeExpirationDate}
            disabled={disabled}
            slotProps={{
              textField: {
                size: 'small',
                error: expirationDateError ? true : false,
                InputProps: {
                  endAdornment: undefined,
                },
              },
            }}
          />
          {(expirationDateError?.message ? true : false) && <FormHelperText error>{expirationDateError?.message}</FormHelperText>}
        </FormControl>
      </Grid>
      <Grid item xs={4}></Grid>
      {(docsUrls ?? []).length > 0 && (
        <Grid item xs={4} sx={{ display: 'flex', alignItems: 'flex-end' }}>
          <Button variant="outlined" size="small" fullWidth startIcon={<FontAwesomeIcon icon={faIdCard} />} onClick={() => documentShowToggleClick()}>
            {!documentShow ? t('pms.booking.reservation.guest.show_documents') : t('pms.booking.reservation.guest.hide_documents')}
          </Button>
        </Grid>
      )}
      {documentShow && (
        <>
          {(docsUrls?.length ?? 0) > 0 && (
            <Grid item xs={24} container>
              <Grid item xs={6}>
                <img src={docsUrls?.[0]} style={{ maxWidth: '100%', height: 'auto' }} />
              </Grid>
              {(docsUrls?.length ?? 0) > 1 && (
                <Grid item xs={6}>
                  <img src={docsUrls?.[1]} style={{ maxWidth: '100%', height: 'auto' }} />
                </Grid>
              )}
            </Grid>
          )}
          {(docsUrls?.length ?? 0) <= 0 && (
            <Grid item xs={24} container>
              <Stack direction="row" justifyContent="flex-start" alignItems="center" spacing={0.5}>
                <FontAwesomeIcon icon={faWarning} color="var(--warning)" />
                <Typography variant="body">{t('pms.booking.reservation.guest.missing_id_documents')}</Typography>
              </Stack>
            </Grid>
          )}
        </>
      )}
    </Grid>
  )
}

function checkNull(value: Partial<GuestIdentificationDocument>): GuestIdentificationDocument | null {
  if (value?.documentId || value?.expirationDate || value?.number || value?.countryId || value?.city) {
    return {
      documentId: value?.documentId || null,
      expirationDate: value?.expirationDate || null,
      number: value?.number || null,
      countryId: value?.countryId || null,
      city: value?.city || null,
    }
  }
  return null
}
